import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormField,
  FormMessage,
  RadioGroup,
  RadioGroupItem,
  Alert,
  AlertTitle,
  AlertDescription,
} from '@predoc/cow';
import { Info, Pencil } from 'lucide-react';
import { useUpdateForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  User,
  EditUserFormData,
  editUserSchema,
  EditFormFieldProps,
} from '../types';
import { UserInviteStatus, UserStatus, ROLES, UserRole } from '../constants';
import { useUpdateUser } from '../hooks/usersApi';
import { toast } from 'sonner';
import { ProviderTypesDropdown } from './ProviderTypesDropdown';
import { ProfileAccessDropdown } from './ProfileAccessDropdown';
import { Permission, useHasPermission } from '@/utils/roles';

export const EditUserModal = ({ user }: { user: User }) => {
  const { hasPermission, currentUser } = useHasPermission(
    Permission.EDIT_USERS,
  );
  const [open, setOpen] = useState(false);
  const form = useUpdateForm<EditUserFormData>({
    defaultValues: {
      ...user,
      providerTypeId: user.providerTypeId || '',
      profileAccess: user.profileAccess?.map((p) => p.id) || [],
    },
    mode: 'onSubmit',
    resolver: zodResolver(editUserSchema),
  });
  const { mutate: updateUser, isPending } = useUpdateUser();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSubmit = (dirtyValues: Partial<EditUserFormData>) => {
    const { profileAccess, providerTypeId, role, ...rest } = dirtyValues;

    const profileAccessChanged = profileAccess !== undefined;

    updateUser(
      {
        userId: user.id,
        ...rest,
        ...(role !== undefined && { role }),
        ...(providerTypeId !== undefined && {
          providerTypeId: providerTypeId || undefined,
        }),
        ...(profileAccessChanged && {
          orgProfileIds: profileAccess,
          // Backend refine requires role when orgProfileIds is set; include
          // current role if it wasn't part of the dirty values.
          role: role ?? user.role,
        }),
      },
      {
        onSuccess: () => {
          toast.success('User updated successfully');
          handleClose();
        },
        onError: (error) => {
          toast.error('Failed to update user');
          console.error(error);
        },
      },
    );
  };

  const canEditEmail =
    (user.status === UserStatus.ACTIVE ||
      user.status === UserStatus.UNRESPONSIVE) &&
    user.invitationStatus !== UserInviteStatus.ACCEPTED;

  // hide edit user button if user does not have permission
  if (!hasPermission) {
    return null;
  }

  return (
    <>
      <Button variant="outline" onClick={handleOpen} aria-label="Edit user">
        <Pencil className="size-4" aria-hidden="true" />
        Edit
      </Button>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="mx-auto !max-w-[946px]">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-xl font-semibold">
              Edit User
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleUpdateSubmit(handleSubmit)}>
              {/** input section */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }: EditFormFieldProps<'firstName'>) => (
                    <FormItem>
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          aria-label="First Name"
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }: EditFormFieldProps<'lastName'>) => (
                    <FormItem>
                      <FormLabel>Last Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          aria-label="Last Name"
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="emailAddress"
                  disabled={!canEditEmail}
                  render={({ field }: EditFormFieldProps<'emailAddress'>) => (
                    <FormItem>
                      <FormLabel>Email Address *</FormLabel>
                      <FormControl>
                        <Input
                          type="emailAddress"
                          {...field}
                          aria-label="Email Address"
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="providerTypeId"
                  render={({ field }: EditFormFieldProps<'providerTypeId'>) => (
                    <FormItem>
                      <FormLabel>Provider Type</FormLabel>
                      <FormControl>
                        <ProviderTypesDropdown
                          enabled={open}
                          value={field.value ?? ''}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/** role section */}
              <FormField
                control={form.control}
                name="role"
                render={({ field }: EditFormFieldProps<'role'>) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        {ROLES.map(({ label, value, description }) => {
                          const isDisabled =
                            value === UserRole.OWNER &&
                            currentUser?.role !== UserRole.OWNER;
                          return (
                            <div
                              key={value}
                              className={`mb-4 flex items-start gap-2 ${isDisabled ? 'opacity-50' : ''}`}
                            >
                              <RadioGroupItem
                                value={value}
                                aria-label={label}
                                id={`radio-${value.toLowerCase().replace(/\s+/g, '-')}`}
                                className="mt-1"
                                disabled={isDisabled}
                              />
                              <label
                                htmlFor={`radio-${value.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`flex flex-col ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                <span className="text-foreground text-sm font-medium">
                                  {label}
                                </span>
                                <span className="text-muted-foreground text-sm">
                                  {description}
                                </span>
                              </label>
                            </div>
                          );
                        })}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />
              {user.isSoleOwner ? (
                <Alert variant="accent" className="mb-8">
                  <Info className="size-4" />
                  <AlertTitle>
                    This user is the sole organization owner.
                  </AlertTitle>
                  <AlertDescription>
                    Promote a different user to the Organization Owner role
                    prior to changing this user’s role.
                  </AlertDescription>
                </Alert>
              ) : null}
              <FormField
                control={form.control}
                name="profileAccess"
                render={({ field }: EditFormFieldProps<'profileAccess'>) => {
                  const currentRole = form.watch('role');
                  const isOwner = currentRole === UserRole.OWNER;

                  return (
                    <FormItem className="mb-6 w-1/2">
                      <FormLabel>Profile Access *</FormLabel>
                      <FormControl>
                        <ProfileAccessDropdown
                          enabled={open}
                          values={field.value || []}
                          onChange={field.onChange}
                          disabled={isOwner}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                      <span className="text-muted-foreground text-xs">
                        The user will have access to these profiles
                      </span>
                    </FormItem>
                  );
                }}
              />
              <DialogFooter className="border-t pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  aria-label="Cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  aria-label="Save"
                  disabled={isPending}
                  loading={isPending}
                >
                  Save
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
